/** Full Kelly fraction of bankroll, floored at 0 (never bet on a negative-EV price). */
export function kellyFraction(p, price) {
  if (price <= 1) return 0;
  return Math.max((p * price - 1) / (price - 1), 0);
}

/**
 * WHAT A LEG IS WORTH, IN THE ONLY UNIT THAT ADDS UP: the expected log growth
 * of a bankroll staked at full Kelly on it.
 *
 *   g = p·ln(1 + f*·(price − 1)) + (1 − p)·ln(1 − f*),  f* = max(0, kelly)
 *
 * The engine used to rank legs by `edge = pModel − pFair`, which is probability
 * POINTS, and points are not comparable across prices (engine review #2). Three
 * points at $1.55 is a Kelly fraction of 8.5% of the bankroll; three points at
 * $13 is 3.25% — the same "edge", a quarter of the growth, and four times the
 * variance. Sorting on points ranked them equal and then broke the tie on a
 * hash, so the app was as likely to print the long shot as the favourite.
 *
 * Growth is the figure that knows the difference, because it is the thing a
 * punter is actually trying to maximise: what repeatedly betting this leg does
 * to the bankroll. It is ZERO for any leg with no edge to bet (f* = 0), which
 * is every leg of every no-model strategy — so those strategies tie at zero
 * exactly as they tied at zero on `edge`, and fall through to the same seeded
 * tie-break they always did. Nothing about a shape strategy moves.
 *
 * `edge` and `ev` stay on the candidate untouched: they are what the screens
 * print, and this is what the pad ranks by.
 */
export function expectedLogGrowth(p, price) {
  if (!Number.isFinite(p) || !Number.isFinite(price)) return 0;
  const f = kellyFraction(p, price);
  if (!(f > 0)) return 0;
  // A certainty stakes the lot; a loss on it is a total loss, and −Infinity is
  // the honest sort key for that rather than a NaN.
  if (f >= 1) return -Infinity;
  return p * Math.log(1 + f * (price - 1)) + (1 - p) * Math.log(1 - f);
}

// The one place these numbers are typed in. `stakeFor`'s own defaults read
// from here, and so does anything that needs to print a default back to a
// punter — a probe reading them out of `stakeFor` was a proxy for exporting
// them and could drift the moment either default changed.
export const KELLY_DEFAULTS = { fraction: 0.25, capPct: 0.02, exposurePct: 0.1 };

/**
 * The staking block a strategy that MEASURES AN EDGE carries (engine review
 * #13). Flat staking spends the same money on a 1% edge and a 9% one, which
 * throws away the only thing a screen with an opinion has to say about size.
 *
 * It is the whole of what those documents say about staking, so it is written
 * once and spread into each of them: six strategies with six copies of three
 * numbers is three numbers that drift. A pad with no bankroll in Settings
 * stakes every one of them flat and says so, which is what they all did before.
 */
export const KELLY_STAKING = { type: 'fractional-kelly', ...KELLY_DEFAULTS };

/**
 * HALF THE FRACTION ON A MULTI, and for the same reason the shrink table exists
 * (engine review #10; `SHRINK_BY_LEGS` in combine.js).
 *
 * Kelly's arithmetic assumes the probability handed to it is right. On a multi
 * it is a product of estimates that share a board, a devig and a fitted state,
 * so it is wrong in one direction on every leg at once — and Kelly punishes an
 * over-stated probability far harder than it rewards an under-stated one. The
 * shrink takes the over-claim out of the number; this takes the rest out of the
 * stake, because a bet whose probability is the least trustworthy on the pad
 * should not also be the biggest one on it.
 *
 * Half, flat, whatever the leg count. The shrink already scales with legs, and
 * multiplying two schedules together would stake a five-leg ticket at a
 * fraction nobody chose.
 */
export const KELLY_MULTI_CAP = 0.5;

/**
 * THE DAY'S EXPOSURE IS ONE BUDGET, SHARED (engine review #13).
 *
 * `stakedSoFar` was a local inside `runStrategy`, so every strategy on the pad
 * started the day with the whole exposure allowance to itself: six strategies
 * at 10% of bankroll is 60% of the bankroll on the counter, and nothing in the
 * app was adding it up. The budget is created ONCE per deal (store.js) and
 * handed to every run through `ctx.budget`, so the tickets spend the same
 * dollars in the order they are printed.
 *
 * A plain mutable object on purpose: it is passed through a context, written by
 * whichever run is printing, and read by the next one. Nothing here is
 * reactive, and a fresh budget is a fresh day.
 */
export const createBudget = () => ({ staked: 0 });

/** What is left of `exposurePct` of the bankroll, never below zero. */
export const budgetLeft = (budget, bankroll, exposurePct) =>
  Math.max(0, exposurePct * bankroll - (budget?.staked ?? 0));

/**
 * The smallest ticket worth printing. Below this the budget has nothing left to
 * say: a 40c multi is not a bet anybody places, and printing one reads as free
 * money rather than as "the day is spent".
 */
export const MIN_STAKE = 1;

/**
 * Stake for one bet: a fraction of full Kelly, capped by a percent of
 * bankroll, an absolute ceiling, and whatever exposure budget remains.
 * Rounded to cents; never negative.
 *
 * `legs` is how many legs the bet is. Anything above one is a multi and takes
 * `KELLY_MULTI_CAP` of the fraction it asked for.
 */
export function stakeFor({
  p,
  price,
  bankroll,
  fraction = KELLY_DEFAULTS.fraction,
  capPct = KELLY_DEFAULTS.capPct,
  maxStake = Infinity,
  exposureLeft = Infinity,
  legs = 1,
}) {
  const fStar = kellyFraction(p, price);
  const used = legs > 1 ? fraction * KELLY_MULTI_CAP : fraction;
  const raw = Math.min(used * fStar * bankroll, capPct * bankroll, maxStake, exposureLeft);
  return Math.round(Math.max(raw, 0) * 100) / 100;
}
